/**
 * LiveHistoryContext
 *
 * Persiste o histórico de canais live assistidos por usuário.
 * Diferente do WatchHistoryContext (filmes/séries), este é específico
 * para live TV onde não há position/duration — apenas lastWatchedAt.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { loadProfileStorage, migrateAllProfileLists, profileStorageKey } from '../utils/profileStorage';

export interface LiveHistoryItem {
  streamId: string;
  channelName: string;
  icon?: string;
  categoryId?: string;
  containerExtension?: string;
  lastWatchedAt: number;
}

interface LiveHistoryContextType {
  items: LiveHistoryItem[];
  /** Registra ou atualiza a visualização de um canal */
  markWatched: (item: Omit<LiveHistoryItem, 'lastWatchedAt'>) => void;
  /** Retorna o timestamp de quando o canal foi assistido, ou null */
  getLastWatched: (streamId: string) => number | null;
  /** Lista canais ordenados do mais recente ao mais antigo */
  getRecentChannels: (limit?: number) => LiveHistoryItem[];
  clearAllLocal: () => void;
}

const LiveHistoryContext = createContext<LiveHistoryContextType | undefined>(undefined);

const STORAGE_PREFIX = 'ott_player_live_history:';
const MAX_ITEMS = 200;

const safeLoad = (key: string): LiveHistoryItem[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is LiveHistoryItem =>
        !!item &&
        typeof item.streamId === 'string' &&
        typeof item.channelName === 'string' &&
        typeof item.lastWatchedAt === 'number'
    );
  } catch {
    return [];
  }
};

export const LiveHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authData } = useAuth();
  const username = authData?.user.username;
  const storageKey = useMemo(
    () => profileStorageKey(STORAGE_PREFIX, authData?.profileId, username) || `${STORAGE_PREFIX}guest`,
    [authData?.profileId, username],
  );
  const legacyStorageKey = username ? `${STORAGE_PREFIX}${username}` : null;
  const [items, setItems] = useState<LiveHistoryItem[]>([]);

  useEffect(() => {
    if (authData?.profileId) {
      migrateAllProfileLists<LiveHistoryItem>(
        storageKey,
        STORAGE_PREFIX,
        (item) => item.streamId,
        (item) => item.lastWatchedAt,
      );
    } else {
      loadProfileStorage(storageKey, legacyStorageKey);
    }
    setItems(safeLoad(storageKey));
  }, [authData?.profileId, legacyStorageKey, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch {
      // noop: persistência oportunística
    }
  }, [items, storageKey]);

  const markWatched = useCallback((item: Omit<LiveHistoryItem, 'lastWatchedAt'>) => {
    setItems((prev) => {
      const filtered = prev.filter((e) => e.streamId !== item.streamId);
      const entry: LiveHistoryItem = { ...item, lastWatchedAt: Date.now() };
      return [entry, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  const getLastWatched = useCallback(
    (streamId: string) => {
      const found = items.find((e) => e.streamId === streamId);
      return found ? found.lastWatchedAt : null;
    },
    [items]
  );

  const getRecentChannels = useCallback(
    (limit = 50) =>
      [...items].sort((a, b) => b.lastWatchedAt - a.lastWatchedAt).slice(0, limit),
    [items]
  );

  const clearAllLocal = useCallback(() => {
    setItems([]);
    try { localStorage.setItem(storageKey, JSON.stringify([])); } catch { /* ignore */ }
  }, [storageKey]);

  const value = useMemo<LiveHistoryContextType>(
    () => ({ items, markWatched, getLastWatched, getRecentChannels, clearAllLocal }),
    [items, markWatched, getLastWatched, getRecentChannels, clearAllLocal]
  );

  return (
    <LiveHistoryContext.Provider value={value}>{children}</LiveHistoryContext.Provider>
  );
};

export const useLiveHistory = (): LiveHistoryContextType => {
  const ctx = useContext(LiveHistoryContext);
  if (!ctx) throw new Error('useLiveHistory must be used within LiveHistoryProvider');
  return ctx;
};
